/**
 * Permission Constants
 * All available permissions in the system
 */
export const PERMISSIONS = {
  // Animals (الحيوانات)
  ANIMALS_READ: "animals.read",
  ANIMALS_CREATE: "animals.create",
  ANIMALS_UPDATE: "animals.update",
  ANIMALS_DELETE: "animals.delete",
  ANIMALS_COST: "animals.cost",

  // Excluded (المستبعدة)
  EXCLUDED_READ: "excluded.read",
  EXCLUDED_CREATE: "excluded.create",
  EXCLUDED_UPDATE: "excluded.update",
  EXCLUDED_DELETE: "excluded.delete",

  // Treatments (العلاجات)
  TREATMENTS_READ: "treatments.read",
  TREATMENTS_CREATE: "treatments.create",
  TREATMENTS_UPDATE: "treatments.update",
  TREATMENTS_DELETE: "treatments.delete",

  // Vaccines (اللقاحات)
  VACCINES_READ: "vaccines.read",
  VACCINES_CREATE: "vaccines.create",
  VACCINES_UPDATE: "vaccines.update",
  VACCINES_DELETE: "vaccines.delete",

  // Feed (التغذية)
  FEED_READ: "feed.read",
  FEED_CREATE: "feed.create",
  FEED_UPDATE: "feed.update",
  FEED_DELETE: "feed.delete",

  // Breeding (التكاثر)
  BREEDING_READ: "breeding.read",
  BREEDING_CREATE: "breeding.create",
  BREEDING_UPDATE: "breeding.update",
  BREEDING_DELETE: "breeding.delete",

  // Mating (التزاوج)
  MATING_READ: "mating.read",
  MATING_CREATE: "mating.create",
  MATING_UPDATE: "mating.update",
  MATING_DELETE: "mating.delete",

  // Weight (الوزن)
  WEIGHT_READ: "weight.read",
  WEIGHT_CREATE: "weight.create",
  WEIGHT_UPDATE: "weight.update",
  WEIGHT_DELETE: "weight.delete",

  // Reports & Statistics (التقارير والإحصائيات)
  REPORTS_VIEW: "reports.view",
  STATISTICS_VIEW: "statistics.view",

  // Employees (الموظفين)
  EMPLOYEES_READ: "employees.read",
  EMPLOYEES_MANAGE: "employees.manage",

  // Roles (الأدوار)
  ROLES_READ: "roles.read",
  ROLES_MANAGE: "roles.manage",

  // Settings (الإعدادات)
  SETTINGS_READ: "settings.read",
  SETTINGS_MANAGE: "settings.manage",

  // Support (الدعم)
  SUPPORT_READ: "support.read",
  SUPPORT_MANAGE: "support.manage",

  // Owner (المالك - صلاحية خاصة)
  OWNER: "*", // يمنح جميع الصلاحيات
};

/**
 * Permission Groups
 * Groups permissions by category for easier management
 */
export const PERMISSION_GROUPS = {
  ANIMALS: [
    PERMISSIONS.ANIMALS_READ,
    PERMISSIONS.ANIMALS_CREATE,
    PERMISSIONS.ANIMALS_UPDATE,
    PERMISSIONS.ANIMALS_DELETE,
    PERMISSIONS.ANIMALS_COST,
  ],
  EXCLUDED: [
    PERMISSIONS.EXCLUDED_READ,
    PERMISSIONS.EXCLUDED_CREATE,
    PERMISSIONS.EXCLUDED_UPDATE,
    PERMISSIONS.EXCLUDED_DELETE,
  ],
  TREATMENTS: [
    PERMISSIONS.TREATMENTS_READ,
    PERMISSIONS.TREATMENTS_CREATE,
    PERMISSIONS.TREATMENTS_UPDATE,
    PERMISSIONS.TREATMENTS_DELETE,
  ],
  VACCINES: [
    PERMISSIONS.VACCINES_READ,
    PERMISSIONS.VACCINES_CREATE,
    PERMISSIONS.VACCINES_UPDATE,
    PERMISSIONS.VACCINES_DELETE,
  ],
  FEED: [
    PERMISSIONS.FEED_READ,
    PERMISSIONS.FEED_CREATE,
    PERMISSIONS.FEED_UPDATE,
    PERMISSIONS.FEED_DELETE,
  ],
  BREEDING: [
    PERMISSIONS.BREEDING_READ,
    PERMISSIONS.BREEDING_CREATE,
    PERMISSIONS.BREEDING_UPDATE,
    PERMISSIONS.BREEDING_DELETE,
  ],
  MATING: [
    PERMISSIONS.MATING_READ,
    PERMISSIONS.MATING_CREATE,
    PERMISSIONS.MATING_UPDATE,
    PERMISSIONS.MATING_DELETE,
  ],
  WEIGHT: [
    PERMISSIONS.WEIGHT_READ,
    PERMISSIONS.WEIGHT_CREATE,
    PERMISSIONS.WEIGHT_UPDATE,
    PERMISSIONS.WEIGHT_DELETE,
  ],
  REPORTS: [
    PERMISSIONS.REPORTS_VIEW,
    PERMISSIONS.STATISTICS_VIEW,
  ],
  EMPLOYEES: [
    PERMISSIONS.EMPLOYEES_READ,
    PERMISSIONS.EMPLOYEES_MANAGE,
  ],
  ROLES: [
    PERMISSIONS.ROLES_READ,
    PERMISSIONS.ROLES_MANAGE,
  ],
  SETTINGS: [
    PERMISSIONS.SETTINGS_READ,
    PERMISSIONS.SETTINGS_MANAGE,
  ],
};

/**
 * Get all permissions as an array
 * @returns {string[]} Array of all permission values
 */
export const getAllPermissions = () => {
  return Object.values(PERMISSIONS);
};

/**
 * Check if user has a specific permission
 * @param {string[]} userPermissions - User's permissions array
 * @param {string} permission - Permission to check
 * @returns {boolean}
 */
export const hasPermission = (userPermissions, permission) => {
  if (!userPermissions || !Array.isArray(userPermissions)) return false;
  
  // Owner has all permissions
  if (userPermissions.includes(PERMISSIONS.OWNER)) return true;
  
  return userPermissions.includes(permission);
};

/**
 * Get display name for a permission
 * Converts "animals.read" to "Animals Read"
 * @param {string} permission - Permission string (e.g., "animals.read")
 * @returns {string} Display name (e.g., "Animals Read")
 */
export const getPermissionDisplayName = (permission) => {
  if (!permission || typeof permission !== "string") return permission;
  
  // Handle owner permission
  if (permission === PERMISSIONS.OWNER) return "Owner (All Permissions)";
  
  // Split by dot and capitalize each word
  const parts = permission.split(".");
  const displayParts = parts.map(part => {
    // Capitalize first letter
    return part.charAt(0).toUpperCase() + part.slice(1);
  });
  
  return displayParts.join(" ");
};

